import { FC } from 'react';
import classNames from 'classnames';
import { NavigateFunction } from 'react-router-dom';
import { SparklesIcon } from '@heroicons/react/outline';

import {
  ActivityType,
  ProgramActivityDataFragment,
  UserType,
} from '../../../generated/graphql';

import { getTimeStamp } from '../../lib/time';
import { activityTypeDisplay } from '../../lib/activity';
import { programActivityTitle } from '../../lib/programActivity';

import Button from '../Button';
import {
  getProgramActivityStatus,
  ProgramActivityStatus,
  ProgramActivityStatusOption,
} from '../ProgramActivityStatus';
import { ProgramActivityThumbnail } from '../ProgramActivityThumbnail';
import { isProviderUserCurrentSigner } from '../ProgramActivityCard/helpers';
import VerticalSignatureDisplay from '../ProgramActivityCard/VerticalSignatureDisplay';
import ActionButtons from './ActionButtons';

import { ProgramActivities } from '.';

const ActivitySummary: FC<{
  programActivity: ProgramActivityDataFragment;
  authedProviderUserId?: string;
}> = ({ programActivity, authedProviderUserId }) => {
  const title = programActivityTitle(programActivity);
  const subtitle = programActivity.providerNote;

  return (
    <div className="flex min-w-0 flex-1 flex-col text-green-150">
      {/* Anchor row: sets the vertical center line at 48px height (icon/title row) */}
      <div className="flex h-12 items-center">
        <ProgramActivityThumbnail
          programActivity={programActivity}
          className="mr-4 h-12 w-12 flex-shrink-0"
        />
        <div className="min-w-0">
          <div className="text-caption font-medium line-clamp-2">{title}</div>
          {subtitle && (
            <div className="text-small-caption text-neutral-125 line-clamp-1">
              {subtitle}
            </div>
          )}
        </div>
      </div>

      {/* Signatures live below the anchor line and can increase row height */}
      <VerticalSignatureDisplay
        programActivity={programActivity}
        className="mt-1 ml-16"
        authedProviderUserId={authedProviderUserId}
      />
    </div>
  );
};

const ActivityRow: FC<{
  programActivity: ProgramActivities[number];
  onClickProgramActivity: (
    programActivity: ProgramActivityDataFragment,
  ) => void;
  patientResourcesMode?: boolean;
  authedProviderUserId?: string;
  patientName?: string;
  programId?: string;
  navigate: NavigateFunction;
  onExpandResponses?: (programActivity: ProgramActivityDataFragment) => void;
  onTextOpen?: (
    activityText: ProgramActivityDataFragment['activityText'],
  ) => void;
  onMarkComplete?: (programActivity: ProgramActivityDataFragment) => void;
  isCompletingProgramActivity?: boolean;
}> = ({
  programActivity,
  onClickProgramActivity,
  patientResourcesMode = false,
  authedProviderUserId,
  patientName,
  programId,
  navigate,
  onExpandResponses,
  onTextOpen,
  onMarkComplete,
  isCompletingProgramActivity = false,
}) => {
  const isCurrentSigner = isProviderUserCurrentSigner(
    programActivity,
    authedProviderUserId,
  );

  const isNotVoided = !programActivity.voidedAt;
  const isIncomplete =
    !programActivity.completedAt && !programActivity.voidedAt;
  const patientStatus = patientResourcesMode
    ? getProgramActivityStatus(programActivity, UserType.Patient)
    : null;
  const isMultipleSig =
    (programActivity.programAssessment?.programAssessmentSignatureUsers
      ?.length ?? 0) > 1;

  const showFillOut =
    patientResourcesMode &&
    isIncomplete &&
    (programActivity.activityType === ActivityType.Assessment
      ? patientStatus === ProgramActivityStatusOption.Todo
      : programActivity.activityType === ActivityType.PdfForm ||
        programActivity.activityType === ActivityType.Esign);

  const fillOutTitle =
    programActivity.activityType === ActivityType.Assessment && isMultipleSig
      ? 'Fill out & Sign'
      : 'Fill out';

  const showComplete =
    patientResourcesMode &&
    Boolean(onMarkComplete) &&
    programActivity.isCompletable &&
    isIncomplete &&
    programActivity.activityType !== ActivityType.Assessment &&
    programActivity.activityType !== ActivityType.PdfForm &&
    programActivity.activityType !== ActivityType.Esign;

  const handleFillOut = () => {
    if (programActivity.activityType === ActivityType.Assessment) {
      navigate(`/client/client-form/${programActivity.id}`);
      return;
    }

    onClickProgramActivity(programActivity);
  };

  return (
    <tr
      className={classNames(
        'h-[72px] cursor-pointer text-ellipsis text-caption text-green-150 hover:bg-neutral-25',
      )}
      onClick={() => onClickProgramActivity(programActivity)}
    >
      <td className="max-w-xs">
        <ActivitySummary
          programActivity={programActivity}
          authedProviderUserId={authedProviderUserId}
        />
      </td>

      <td className="min-w-[80px]">
        <div className="-mt-px flex h-12 items-center">
          {activityTypeDisplay(programActivity)}
        </div>
      </td>

      <td className="min-w-[140px]">
        <div className="-mt-px flex h-12 items-center">
          <ProgramActivityStatus
            programActivity={programActivity}
            className="text-small-caption"
            userType={
              patientResourcesMode ? UserType.Patient : UserType.ProviderUser
            }
          />
          {isCurrentSigner && isNotVoided && (
            <Button
              title="Complete & Sign"
              size="extra-small"
              theme="secondary"
              className="ml-3 h-[20px] py-0.5 !px-2.5"
              IconComponent={SparklesIcon}
              iconPosition="left"
              iconClassName="h-3 w-3 !mr-1"
              onClick={() => {
                navigate(
                  `/provider-form/${programActivity?.programAssessment?.id}`,
                  {
                    state: {
                      shouldNavigateBack: true,
                    },
                  },
                );
              }}
            />
          )}
        </div>
      </td>

      <td className="min-w-[130px]">
        <div className="-mt-px flex h-12 items-center">
          {getTimeStamp(programActivity.sentAt, false)}
        </div>
      </td>

      <td className="min-w-[130px]">
        <div className="-mt-px flex h-12 items-center">
          {programActivity.completedAt
            ? getTimeStamp(programActivity.completedAt, false)
            : '-'}
        </div>
      </td>

      <td className="min-w-[120px]">
        <ActionButtons
          programActivity={programActivity}
          userType={
            patientResourcesMode ? UserType.Patient : UserType.ProviderUser
          }
          authedProviderUserId={authedProviderUserId}
          patientName={patientName}
          programId={programId}
          onExpandResponses={() => onExpandResponses?.(programActivity)}
          onTextOpen={onTextOpen}
          onFillOut={showFillOut ? handleFillOut : undefined}
          fillOutTitle={fillOutTitle}
          onMarkComplete={
            showComplete ? () => onMarkComplete?.(programActivity) : undefined
          }
          isCompletingProgramActivity={isCompletingProgramActivity}
        />
      </td>
    </tr>
  );
};

export default ActivityRow;
